/**
 * ServicesDirectory — one organized launcher for every service on the
 * platform, grouped the way a ticket-platform operator thinks.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import {
  Compass, Receipt, Ticket, Tag, Users, Briefcase, Handshake, FileText,
  BookOpen, PenTool, Bot, Megaphone, Globe, MapPin, Building, Landmark,
  Link2, BarChart3, FolderTree, MessageSquare, Settings, UserPlus,
  CalendarDays, Package, Boxes, Shield, Trophy, Sparkles,
} from 'lucide-react';

const GROUPS = [
  {
    title: 'Ticketing & Revenue',
    items: [
      { icon: Receipt, label: 'All Transactions', path: '/admin/transactions' },
      { icon: CalendarDays, label: 'Events Manager', path: '/admin/events' },
      { icon: Ticket, label: 'Ticket Holders CRM', path: '/admin/ticket-holders' },
      { icon: Tag, label: 'Promo Codes', path: '/admin/promo-codes' },
      { icon: Handshake, label: 'Promo Partners', path: '/admin/promo-partners/credit-order' },
    ],
  },
  {
    title: 'Business Services',
    items: [
      { icon: Briefcase, label: 'Managed Services', path: '/admin/managed' },
      { icon: Users, label: 'CRM', path: '/admin/crm' },
      { icon: BarChart3, label: 'Sales Pipeline', path: '/admin/pipeline' },
      { icon: FileText, label: 'New Proposal', path: '/admin/proposals/new' },
      { icon: Package, label: 'Products & Services', path: '/admin/products' },
      { icon: Boxes, label: 'Rentals', path: '/admin/rentals' },
    ],
  },
  {
    title: 'Content & Editorial',
    items: [
      { icon: BookOpen, label: 'Editorial Guides', path: '/admin/guides' },
      { icon: PenTool, label: 'Blog Manager', path: '/admin/blog' },
      { icon: Bot, label: 'Editorial Bot', path: '/admin/editorial-bot' },
      { icon: Megaphone, label: 'Ad Creator', path: '/admin/ad-creator' },
    ],
  },
  {
    title: 'Discovery & Growth',
    items: [
      { icon: Bot, label: 'EventismBot', path: '/admin/eventism-bot' },
      { icon: MapPin, label: 'Markets', path: '/admin/markets' },
      { icon: Landmark, label: 'Venues', path: '/admin/venues' },
      { icon: Building, label: 'Brands', path: '/admin/brands' },
      { icon: Globe, label: 'SEO Indexing', path: '/admin/indexing' },
      { icon: Link2, label: 'Redirects', path: '/admin/redirects' },
      { icon: FolderTree, label: 'Site Directory', path: '/admin/site-directory' },
      { icon: BarChart3, label: 'Site Analytics', path: '/admin/analytics' },
      { icon: Megaphone, label: 'Marketing', path: '/admin/marketing' },
      { icon: Trophy, label: 'October Challenge', path: '/admin/october' },
    ],
  },
  {
    title: 'People & Operations',
    items: [
      { icon: UserPlus, label: 'User Directory', path: '/admin/users' },
      { icon: Shield, label: 'Team & Access', path: '/admin/team' },
      { icon: Users, label: 'Stakeholders', path: '/admin/stakeholders' },
      { icon: MessageSquare, label: 'SMS Inbox', path: '/admin/sms-inbox' },
      { icon: Sparkles, label: 'Partner Invitations', path: '/admin/partner-invitations' },
      { icon: Settings, label: 'Settings', path: '/admin/settings' },
    ],
  },
];

export default function ServicesDirectory() {
  const navigate = useNavigate();
  return (
    <Card className="bg-card border-border" data-testid="services-directory">
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          <Compass className="h-5 w-5 text-indigo-400" /> All Services
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {GROUPS.map(group => (
          <div key={group.title}>
            <p className="text-[10px] font-bold text-muted-foreground uppercase tracking-widest mb-1.5">{group.title}</p>
            <div className="grid grid-cols-2 gap-1">
              {group.items.map(item => (
                <button key={item.path + item.label}
                  onClick={() => navigate(item.path)}
                  className="flex items-center gap-2 rounded-md px-2 py-1.5 text-left hover:bg-accent transition-colors"
                  data-testid={`svc-${item.label.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                  <item.icon className="h-3.5 w-3.5 text-muted-foreground flex-shrink-0" />
                  <span className="text-xs font-medium truncate">{item.label}</span>
                </button>
              ))}
            </div>
          </div>
        ))}
      </CardContent>
    </Card>
  );
}
